import { ModelVsActual } from '@/dashboards/TitleDashboard/components';
import { ModelDataMetrics } from '@/dashboards/TitleDashboard/components/ModelDataMetrics';
import { ModelStatisticsCard } from '@/dashboards/TitleDashboard/components/ModelStatisticsCard';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';
import { useDashboardLinks } from '@/hooks/useDashboardLinks';
import {
    AnalyticalAlleyLogo,
    Button,
    Spinner,
    Typography,
} from '@analytical-alley/ui';
import { useMatchMedia } from '@analytical-alley/ui/hooks';
import { IconArrowRight } from '@tabler/icons-react';
import { MatchRoute } from '@tanstack/react-router';
import { AiOutlineInfoCircle } from 'react-icons/ai';

export const TitleDashboard = () => {
    const {
        project: { projectInfo, projectLead, dataScientist, name },
        showWelcomeModal,
    } = useDashboardGlobalContext();

    const { isLg } = useMatchMedia(['lg']);

    const { getFirstAccessibleLink } = useDashboardLinks();

    const firstAccessibleLink = getFirstAccessibleLink(['titleDashboard']);

    return (
        <div className="relative flex flex-col justify-center items-start px-40 p-16 lg:p-36 pb-48 dark:text-white max-lg:px-5">
            <AnalyticalAlleyLogo
                size={isLg ? 'large' : 'normal'}
                className="absolute top-5 left-5 lg:top-11 lg:left-11"
            >
                <Typography className="font-light" variant="bodyL" ellipsisTooltip>
                    hello@analyticalalley.com
                </Typography>
            </AnalyticalAlleyLogo>

            <div className="self-center w-full max-w-[1600px] mt-11 max-lg:max-w-full">
                <div className="flex gap-14 max-2xl:flex-col max-xl:gap-12 max-lg:gap-8">
                    <div className="glass-tile flex flex-col p-8 max-lg:px-5 w-6/12 max-2xl:w-full relative">
                        {showWelcomeModal && (
                            <button
                                onClick={showWelcomeModal}
                                className="absolute top-6 right-6 text-white hover:text-gray-300 transition-colors"
                                aria-label="Show welcome information"
                            >
                                <AiOutlineInfoCircle size={24} />
                            </button>
                        )}
                        <Typography
                            as="h1"
                            variant="titleXXL"
                            tooltip="Every great story needs a proper introduction. Get acquainted with the people behind the model delivery, review the business metrics and the timeframe we are using, and verify the model's accuracy and stability."
                        >
                            {name}
                        </Typography>
                        <Typography className="font-mono font-light mt-2">
                            Our mAI-driven analytics boost your ROI and optimize
                            your media mix, ensuring every euro is well spent.{' '}
                            <br /> You’ll see exactly how each marketing
                            activity affects your business, simplifying
                            management and making results predictable. <br />{' '}
                            It’s intelligent marketing made simple.
                        </Typography>
                        <Typography
                            as="h5"
                            variant="titleS"
                            className="mt-8 max-lg:max-w-full"
                        >
                            {projectInfo}
                        </Typography>
                        <div className="justify-between mt-16 max-lg:mt-10 max-lg:max-w-full">
                            <div className="flex flex-wrap gap-5">
                                {dataScientist || projectLead ? (
                                    <div className="flex flex-col grow">
                                        <div className="flex flex-col font-mono grow dark:text-white max-lg:max-w-full">
                                            <Typography
                                                as="h5"
                                                variant="titleS"
                                                className="max-lg:max-w-full font-sans"
                                            >
                                                For any questions and help
                                                contact:
                                            </Typography>
                                            {projectLead && (
                                                <>
                                                    <Typography
                                                        as="h6"
                                                        variant="bodyXL"
                                                        className="mt-8 max-lg:max-w-full font-medium"
                                                        tooltip="Your primary point of contact for project delivery and timelines."
                                                    >
                                                        {
                                                            projectLead.displayName
                                                        }
                                                        , Project Lead
                                                    </Typography>
                                                    <Typography
                                                        variant="bodyL"
                                                        className="mt-2 max-lg:max-w-full font-light"
                                                    >
                                                        {projectLead.email}
                                                    </Typography>
                                                </>
                                            )}
                                            {dataScientist && (
                                                <>
                                                    <Typography
                                                        as="h6"
                                                        variant="bodyXL"
                                                        className="mt-8 max-lg:max-w-full font-medium"
                                                        tooltip="Our expert responsible for making sure the model results are reliable and actionable."
                                                    >
                                                        {
                                                            dataScientist.displayName
                                                        }
                                                        , Data Scientist
                                                    </Typography>
                                                    <Typography
                                                        variant="bodyL"
                                                        className="mt-2 max-lg:max-w-full font-light"
                                                    >
                                                        {dataScientist.email}
                                                    </Typography>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ) : null}
                                <div className="flex flex-col ml-auto mt-auto">
                                    {firstAccessibleLink ? (
                                        <firstAccessibleLink.Link>
                                            {(state) => {
                                                return (
                                                    <Button
                                                        color="light"
                                                        size="lg"
                                                        tooltip="See what's driving your KPI and how"
                                                    >
                                                        {
                                                            firstAccessibleLink.title
                                                        }
                                                        <MatchRoute
                                                            to={state.to}
                                                            includeSearch={
                                                                false
                                                            }
                                                            pending
                                                        >
                                                            {(match) => {
                                                                if (match) {
                                                                    return (
                                                                        <Spinner />
                                                                    );
                                                                }
                                                                return (
                                                                    <IconArrowRight />
                                                                );
                                                            }}
                                                        </MatchRoute>
                                                    </Button>
                                                );
                                            }}
                                        </firstAccessibleLink.Link>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="glass-tile flex flex-col p-8 border-0 border-solid max-lg:px-5 w-6/12 max-2xl:w-full">
                        <Typography
                            as="h1"
                            variant="titleM"
                            className="max-lg:max-w-full"
                        >
                            Data used in this model
                        </Typography>
                        <div className="grid grid-cols-2 gap-4 font-mono items-start max-lg:grid-cols-1 mt-4">
                            <ModelDataMetrics />
                            <ModelStatisticsCard />
                        </div>
                        <ModelVsActual />
                    </div>
                </div>
            </div>
        </div>
    );
};
